import { useState } from 'react';
import { Box, CircularProgress, Snackbar, SnackbarContent } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../core/authContext';
import { DashboardLoader } from '../Dashboard/DashboardLoader';
import { SetupChecklist } from '../Dashboard/components/SetupChecklist';
import { MatchesCallout } from '../Data/MatchesCallout';
import { needsAttention } from '../Data/matchesApi';
import { GeneratePlansDialog } from '../Generate/GeneratePlansDialog';
import { serverSetupStatusSources, setupDone, useMatchesSummary, useSetupStatus } from '../Dashboard/setupStatus';
import { NAV_ROUTES, cameFromAppLink } from './navRoutes';
import { OldLanding } from './OldLanding';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

type Landing = 'reports' | 'dashboard';

const TOAST_BG = '#2C2C52';

function Spinner() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: HEIGHT_BELOW_BARS }}>
      <CircularProgress />
    </Box>
  );
}

export function HomePage() {
  const { hasBambooAccess, isSuperAdmin, isDistrictAdmin, user, impersonation } = useAuth();
  const { state } = useLocation();
  const districtAdmin = hasBambooAccess === true && isDistrictAdmin && !isSuperAdmin;
  const sessionKey = `${user?.id ?? ''}|${impersonation?.sub ?? ''}`;
  const setup = useSetupStatus(districtAdmin, serverSetupStatusSources, sessionKey);
  const matches = useMatchesSummary(districtAdmin, sessionKey);
  const [landing, setLanding] = useState<{ sessionKey: string; landing: Landing | 'undecided' } | null>(null);
  const [plansDialog, setPlansDialog] = useState<{ count: number } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [loadStartedAt] = useState(() => performance.now());
  const evaluationGeneratedAt = setup.phase === 'ready' ? (setup.facts?.programEvaluation?.generatedAt ?? '') : null;
  if (hasBambooAccess === null) {
    return <Spinner />;
  }
  if (!hasBambooAccess) {
    return <OldLanding />;
  }
  if (!isSuperAdmin && !isDistrictAdmin) {
    return <Navigate to={NAV_ROUTES.reports} replace />;
  }
  if (districtAdmin && !cameFromAppLink(state)) {
    const decided = landing !== null && landing.sessionKey === sessionKey ? landing.landing : null;
    const matchesPending = setup.phase === 'ready' && matches.phase === 'pending';
    if (decided === null && (setup.phase === 'pending' || matchesPending)) {
      return <Spinner />;
    }
    if (decided === null || decided === 'undecided') {
      if (setup.phase === 'ready' && setup.facts !== null && !matchesPending) {
        const attention = matches.state !== null && needsAttention(matches.state);
        setLanding({ sessionKey, landing: setupDone(setup.facts) && !attention ? 'reports' : 'dashboard' });
        return <Spinner />;
      }
      if (decided === null && setup.phase === 'failed') {
        setLanding({ sessionKey, landing: 'undecided' });
        return <Spinner />;
      }
    }
    if (decided === 'reports') {
      return <Navigate to={NAV_ROUTES.reports} replace />;
    }
  }
  if (!districtAdmin) {
    return <DashboardLoader loadStartedAt={loadStartedAt} />;
  }
  const openPlansDialog = (count: number) => setPlansDialog({ count });
  const strips = matches.state !== null && (
    <MatchesCallout toReview={matches.state.toReview} heldOutStudents={matches.state.heldOutStudents} />
  );
  return (
    <>
      <DashboardLoader
        checklist={(stats) => (
          <>
            {strips}
            <SetupChecklist
              status={setup}
              onGeneratePlans={openPlansDialog}
              flashlightConnected={stats?.flashlightConnected ?? null}
            />
          </>
        )}
        evaluationGeneratedAt={evaluationGeneratedAt}
        loadStartedAt={loadStartedAt}
      />
      <GeneratePlansDialog
        open={plansDialog !== null}
        studentCount={plansDialog?.count ?? 0}
        onClose={() => setPlansDialog(null)}
        onGenerated={(n) => {
          setPlansDialog(null);
          setToast(`Plans generated for ${n} students.`);
        }}
      />
      {toast && (
        <Snackbar
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          autoHideDuration={8000}
          onClose={(_, reason) => reason !== 'clickaway' && setToast(null)}
        >
          <SnackbarContent
            role="status"
            data-testid="plans-generated-toast"
            message={toast}
            sx={{ bgcolor: TOAST_BG, color: '#FFFFFF', borderRadius: '4px' }}
          />
        </Snackbar>
      )}
    </>
  );
}
